import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../App";
import { ArrowLeft, Building, Cog, Zap, Beaker, Leaf, Factory, Wrench, Loader } from "lucide-react";

const ICONS = {
  building: Building,
  cog: Cog,
  bolt: Zap,
  flask: Beaker,
  leaf: Leaf,
  factory: Factory,
  wrench: Wrench,
  thermometer: Cog,
  flame: Zap,
  droplet: Beaker,
  mountain: Building,
  car: Cog,
};

export default function DisciplineSelection() {
  const { examType } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "practice";
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDisciplines();
  }, [examType]);

  const fetchDisciplines = async () => {
    try {
      const res = await api.get(`/disciplines/${examType}`);
      setDisciplines(res.data);
    } catch (err) {
      console.error("Error fetching disciplines:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (disciplineId) => {
    switch (mode) {
      case "exam":
        navigate(`/mock-exam/${examType}/${disciplineId}`);
        break;
      case "flashcards":
        navigate(`/flashcards/${examType}/${disciplineId}`);
        break;
      default:
        navigate(`/practice/${examType}/${disciplineId}`);
    }
  };

  const getModeTitle = () => {
    switch (mode) {
      case "exam": return "Mock Exam";
      case "flashcards": return "Flashcards";
      default: return "Practice";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate(`/exam-selection${mode ? `?mode=${mode}` : ''}`)}
            className="btn-ghost p-2"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{examType} {getModeTitle()}</h1>
            <p className="text-sm text-slate-500">Select your discipline</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid sm:grid-cols-2 gap-4">
          {disciplines.map((disc) => {
            const IconComp = ICONS[disc.icon] || Wrench;
            return (
              <button
                key={disc.id}
                data-testid={`discipline-${disc.id}`}
                onClick={() => handleSelect(disc.id)}
                className="card text-left hover:shadow-lg transition-all group flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <IconComp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                    {disc.name}
                  </h3>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
